import { useState, useEffect, useCallback, useRef } from "react";
import { httpClient } from "../../HttpClient";
import getPusherClient from "../../pusherClient";
import { showStatusToast } from "../../components/ui/Toast";

// Filet de secours : comble tout ce que Pusher aurait manqué (déconnexion,
// event perdu, onglet resté en arrière-plan...) — jamais de dépendance
// totale au service tiers, voir le plan Discussion.
const POLL_FALLBACK_MS = 20000;
// Délai avant d'afficher un toast de patience sur l'ouverture d'une nouvelle
// discussion — l'ouverture optimiste (voir openConversation) rend déjà l'UI
// instantanée dans le cas normal ; ce toast ne doit apparaître que si la
// création réelle côté serveur traîne réellement.
const OPEN_CONVERSATION_TOAST_DELAY_MS = 600;

// Hook de données de la messagerie interne 1-à-1 (pattern Mails/useGmailAccount.js).
// currentUserId sert uniquement à savoir si un message entrant vient d'autrui
// (pour le marquer lu automatiquement) — jamais envoyé au backend, qui
// dérive tout de req.user côté serveur.
function useDiscussion(currentUserId) {
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState(null);

  const lastMessageIdRef = useRef(null);
  // Promesses de résolution des conversations "temp-*" en cours de création
  // côté serveur (voir openConversation) — permet à sendMessage d'attendre
  // le vrai id si l'utilisateur envoie un message avant que la création
  // n'ait eu le temps de répondre.
  const pendingConversationRef = useRef({});

  const fetchConversations = useCallback(() => {
    setLoadingConversations(true);
    return httpClient
      .get("/api/discussion/conversations")
      .then(({ data }) => setConversations(data.data || []))
      .catch((err) => setError(err.response?.data?.message || "Erreur lors du chargement des conversations."))
      .finally(() => setLoadingConversations(false));
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Insère/màj une conversation dans l'état local et la remonte en tête
  // (ordre "activité récente", cohérent avec le tri serveur) — évite de
  // refaire un GET /discussion/conversations complet après chaque action
  // (créer une conversation, envoyer un message) alors qu'on a déjà toutes
  // les données nécessaires dans la réponse de l'action elle-même.
  const upsertConversationLocal = useCallback((partial) => {
    setConversations((prev) => {
      const existing = prev.find((c) => c.id === partial.id);
      const merged = { unreadCount: 0, lastMessage: null, ...existing, ...partial };
      const rest = prev.filter((c) => c.id !== partial.id);
      return [merged, ...rest];
    });
  }, []);

  const markRead = useCallback((conversationId) => {
    httpClient
      .post(`/api/discussion/conversations/${conversationId}/read`)
      .then(() =>
        setConversations((prev) => prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c))),
      )
      .catch((err) => console.error("Erreur marquage lu Discussion :", err));
  }, []);

  const appendIncoming = useCallback((message) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === message.id)) return prev;
      lastMessageIdRef.current = Math.max(lastMessageIdRef.current || 0, message.id);
      return [...prev, message];
    });
  }, []);

  const selectConversation = useCallback(
    (conversationId) => {
      setSelectedId(conversationId);
      setMessages([]);
      setNextCursor(null);
      lastMessageIdRef.current = null;
      setError(null);

      // Id temporaire (conversation optimiste encore en cours de création,
      // voir openConversation) : rien à charger tant que le vrai id n'est
      // pas connu — évite un appel réseau voué à échouer si l'utilisateur
      // re-clique sur cette entrée pendant qu'elle se crée.
      if (String(conversationId).startsWith("temp-")) {
        setLoadingMessages(false);
        return;
      }

      setLoadingMessages(true);

      httpClient
        .get(`/api/discussion/conversations/${conversationId}/messages`)
        .then(({ data }) => {
          const items = data.data || [];
          setMessages(items);
          setNextCursor(data.nextCursor || null);
          lastMessageIdRef.current = items.length ? items[items.length - 1].id : null;
        })
        .catch((err) => setError(err.response?.data?.message || "Erreur lors du chargement des messages."))
        .finally(() => setLoadingMessages(false));

      markRead(conversationId);
    },
    [markRead],
  );

  // Remonte dans l'historique (messages plus anciens que la page déjà chargée).
  const loadOlderMessages = useCallback(() => {
    if (!selectedId || !nextCursor) return;
    httpClient
      .get(`/api/discussion/conversations/${selectedId}/messages`, { params: { cursor: nextCursor } })
      .then(({ data }) => {
        setMessages((prev) => [...(data.data || []), ...prev]);
        setNextCursor(data.nextCursor || null);
      })
      .catch((err) => console.error("Erreur chargement historique Discussion :", err));
  }, [selectedId, nextCursor]);

  // body et/ou fileMeta ({ url, name, type, size }, déjà uploadé côté
  // navigateur vers Blob — voir MessageComposer.jsx) — un message peut être
  // uniquement une pièce jointe.
  const sendMessage = useCallback(
    async (body, fileMeta) => {
      if (!selectedId || (!body?.trim() && !fileMeta)) return;

      // Conversation encore en cours de création côté serveur (ouverture
      // optimiste, voir openConversation) : on attend le vrai id avant
      // d'envoyer plutôt que de poster sur un id temporaire inexistant.
      let targetId = selectedId;
      const pending = pendingConversationRef.current[selectedId];
      if (pending) {
        targetId = await pending;
      }

      const payload = {};
      if (body?.trim()) payload.body = body.trim();
      if (fileMeta) {
        payload.fileUrl = fileMeta.url;
        payload.fileName = fileMeta.name;
        payload.fileType = fileMeta.type;
        payload.fileSize = fileMeta.size;
      }

      const { data } = await httpClient.post(
        `/api/discussion/conversations/${targetId}/messages`,
        payload,
      );
      const message = data.data;
      appendIncoming(message);
      upsertConversationLocal({ id: targetId, lastMessage: message, updatedAt: message.createdAt });
    },
    [selectedId, appendIncoming, upsertConversationLocal],
  );

  // Ouvre (ou crée) la conversation avec ce collaborateur.
  // Deux cas :
  // - la conversation existe déjà localement (on a déjà échangé avec ce
  //   collaborateur) → sélection immédiate, sans aller-retour réseau (le
  //   POST serait un upsert no-op côté serveur, mais coûte quand même la
  //   latence réseau — évitable puisqu'on a déjà tout en local) ;
  // - sinon → ouverture optimiste instantanée d'une conversation temporaire
  //   (fil vide, composeur actif tout de suite), le temps que le backend
  //   crée la vraie conversation en arrière-plan. Un toast de patience
  //   n'apparaît que si cette création prend réellement du temps.
  const openConversation = useCallback(
    (otherUserId) => {
      const existing = conversations.find((c) => c.otherUserId === otherUserId);
      if (existing) {
        selectConversation(existing.id);
        return Promise.resolve(existing.id);
      }

      const tempId = `temp-${otherUserId}`;
      upsertConversationLocal({
        id: tempId,
        otherUserId,
        lastMessage: null,
        unreadCount: 0,
        updatedAt: new Date().toISOString(),
      });
      setSelectedId(tempId);
      setMessages([]);
      setNextCursor(null);
      lastMessageIdRef.current = null;
      setLoadingMessages(false);
      setError(null);

      let toast = null;
      const toastTimer = setTimeout(() => {
        toast = showStatusToast({
          status: "loading",
          titre: "Ouverture de la discussion...",
          message: "Ça prend un peu plus de temps que prévu, merci de patienter.",
        });
      }, OPEN_CONVERSATION_TOAST_DELAY_MS);

      const creationPromise = httpClient
        .post("/api/discussion/conversations", { otherUserId })
        .then(({ data }) => {
          clearTimeout(toastTimer);
          const real = data.data;
          setConversations((prev) => prev.filter((c) => c.id !== tempId));
          upsertConversationLocal(real);

          let stillOnTemp = false;
          setSelectedId((current) => {
            stillOnTemp = current === tempId;
            return stillOnTemp ? real.id : current;
          });
          // Recharge les messages sur le vrai id : couvre le cas où
          // l'upsert serveur a en fait retrouvé une conversation existante
          // (ex. créée depuis un autre onglet) avec déjà de l'historique.
          if (stillOnTemp) selectConversation(real.id);

          if (toast) toast.update({ status: "success", titre: "Discussion ouverte" });
          return real.id;
        })
        .catch((err) => {
          clearTimeout(toastTimer);
          setConversations((prev) => prev.filter((c) => c.id !== tempId));
          setSelectedId((current) => (current === tempId ? null : current));
          const message = err.response?.data?.message || "Impossible d'ouvrir cette discussion.";
          setError(message);
          if (toast) {
            toast.update({ status: "error", titre: "Échec de l'ouverture", message });
          } else {
            showStatusToast({ status: "error", titre: "Échec de l'ouverture", message });
          }
          throw err;
        })
        .finally(() => {
          delete pendingConversationRef.current[tempId];
        });

      pendingConversationRef.current[tempId] = creationPromise;
      return creationPromise;
    },
    [conversations, upsertConversationLocal, selectConversation],
  );

  // Abonnement temps réel à la conversation ouverte + poll de rattrapage,
  // indépendants l'un de l'autre. Nettoyage complet au changement de
  // conversation/démontage.
  useEffect(() => {
    // Ids "temp-*" : conversation optimiste pas encore créée côté serveur
    // (voir openConversation) — rien à écouter/interroger tant que le vrai
    // id n'est pas connu.
    if (!selectedId || String(selectedId).startsWith("temp-")) return;

    const pusher = getPusherClient();
    const channelName = `private-conversation-${selectedId}`;
    // pusher === null : clé absente/pas encore chargée (voir pusherClient.js)
    // — pas d'abonnement temps réel, le poll ci-dessous reste seul filet.
    const channel = pusher ? pusher.subscribe(channelName) : null;

    const onNewMessage = (message) => {
      appendIncoming(message);
      if (message.senderId !== currentUserId) markRead(selectedId);
    };
    if (channel) channel.bind("new-message", onNewMessage);

    const poll = setInterval(() => {
      httpClient
        .get(`/api/discussion/conversations/${selectedId}/messages`, {
          params: { afterId: lastMessageIdRef.current || 0 },
        })
        .then(({ data }) => (data.data || []).forEach(appendIncoming))
        .catch(() => {}); // silencieux : simple rattrapage périodique, pas d'action utilisateur possible
    }, POLL_FALLBACK_MS);

    return () => {
      if (channel) {
        channel.unbind("new-message", onNewMessage);
        pusher.unsubscribe(channelName);
      }
      clearInterval(poll);
    };
  }, [selectedId, currentUserId, appendIncoming, markRead]);

  return {
    conversations,
    loadingConversations,
    selectedId,
    messages,
    loadingMessages,
    hasMoreMessages: !!nextCursor,
    error,
    fetchConversations,
    selectConversation,
    loadOlderMessages,
    sendMessage,
    openConversation,
  };
}

export default useDiscussion;
